// Contexte audio UNIQUE pour toute la session : jamais recréé sauf s'il est fermé par le navigateur,
// repris (resume) automatiquement après focus / veille / retour en ligne, et à chaque lecture.
let audioCtx = null;
const FLAG = "novanext_audio_unlocked";

function ensureCtx() {
  if (!audioCtx || audioCtx.state === "closed") {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioCtx;
}

function resumeCtx() {
  try {
    const ctx = ensureCtx();
    if (ctx.state !== "running") return ctx.resume().then(notifySubs).catch(() => {});
  } catch (e) {}
  return Promise.resolve();
}

export function isAudioUnlocked() {
  try { return !!audioCtx && audioCtx.state === "running"; } catch (e) { return false; }
}

// Abonnement à l'état RÉEL du contexte (running / suspended) : les pages affichent « Activer le son »
// uniquement quand le son interne est effectivement impossible.
const _subs = new Set();
function notifySubs() { const v = isAudioUnlocked(); _subs.forEach((cb) => { try { cb(v); } catch (e) {} }); }
export function subscribeAudio(cb) {
  _subs.add(cb);
  try { const ctx = ensureCtx(); ctx.onstatechange = notifySubs; } catch (e) {}
  cb(isAudioUnlocked());
  return () => _subs.delete(cb);
}

// Joue `schedule(ctx, now)` une fois le contexte réellement actif (évite les sons perdus pendant "suspended").
function withCtx(schedule) {
  try {
    const ctx = ensureCtx();
    const run = () => { try { schedule(ctx, ctx.currentTime); } catch (e) {} };
    if (ctx.state === "running") run();
    else resumeCtx().then(run);
  } catch (e) {}
}

function tone(ctx, freq, start, dur, type, peak, glideTo) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, start + dur * 0.85);
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(peak, start + 0.03);
  gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + dur + 0.05);
}

export function playChime() {
  withCtx((ctx, now) => [880, 1108.73, 1318.51].forEach((f, i) => tone(ctx, f, now + i * 0.12, 0.5, "sine", 0.22)));
}

// Distinct two-tone "ding-dong" for incoming QR orders (differs from the triad used for calls).
export function playOrderChime() {
  withCtx((ctx, now) => [659.25, 523.25].forEach((f, i) => tone(ctx, f, now + i * 0.22, 0.6, "triangle", 0.28)));
}

// Alarme URGENTE et DISTINCTE (triple bip descendant, onde carrée) pour les incidents de paiement.
export function playAlarm() {
  withCtx((ctx, now) => [0, 0.28, 0.56].forEach((t) => tone(ctx, 466.16, now + t, 0.24, "square", 0.3, 174.61)));
}

// Appel serveur : deux notes brèves montantes (sonnette de table).
export function playCallChime() {
  withCtx((ctx, now) => [[987.77, 0], [1318.51, 0.16]].forEach(([f, t]) => tone(ctx, f, now + t, 0.35, "sine", 0.26)));
}

// Demande d'addition / encaissement : quatre notes rapides ascendantes (tiroir-caisse).
export function playBillChime() {
  withCtx((ctx, now) => [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => tone(ctx, f, now + i * 0.09, 0.32, "triangle", 0.24)));
}

// Plat prêt en cuisine : "ding" long unique suivi d'une note grave (cloche de passe).
export function playReadyChime() {
  withCtx((ctx, now) => { tone(ctx, 1567.98, now, 0.9, "sine", 0.3); tone(ctx, 783.99, now + 0.35, 0.7, "sine", 0.18); });
}

// Choisit le carillon d'une demande client selon son type.
// Demande de modification client : deux notes montantes rapides + une tenue (distinct des appels, commandes et additions).
export function playChangeChime() {
  withCtx((ctx, now) => [587.33, 783.99, 987.77].forEach((f, i) => tone(ctx, f, now + i * 0.09, i === 2 ? 0.7 : 0.25, "triangle", 0.26)));
}

// Stock faible : deux notes graves discrètes (information, pas urgence).
export function playStockChime() {
  withCtx((ctx, now) => [[392.0, 0], [329.63, 0.2]].forEach(([f, t]) => tone(ctx, f, now + t, 0.3, "sine", 0.16)));
}

export function playRequestChime(type) {
  const t = String(type || "").toUpperCase();
  if (t === "CHANGE") return playChangeChime();
  if (t === "STOCK") return playStockChime();
  if (t === "ADDITION" || t === "PAIEMENT" || t === "ENCAISSEMENT") return playBillChime();
  if (t === "SERVEUR" || t === "AIDE") return playCallChime();
  return playChime(); // eau, pain, couverts, autre : triade standard
}

export function unlockAudio() {
  try {
    resumeCtx();
    sessionStorage.setItem(FLAG, "1");
  } catch (e) {}
}

// Débloque l'audio dès le PREMIER geste utilisateur (connexion, PIN, 1er clic) — sans bouton manuel.
let _armed = false;
export function armAudio() {
  if (_armed || typeof document === "undefined") return;
  _armed = true;
  const unlock = () => {
    unlockAudio();
    document.removeEventListener("pointerdown", unlock);
    document.removeEventListener("keydown", unlock);
    document.removeEventListener("touchstart", unlock);
  };
  document.addEventListener("pointerdown", unlock, { once: true });
  document.addEventListener("keydown", unlock, { once: true });
  document.addEventListener("touchstart", unlock, { once: true });
  // Après veille / changement d'onglet / retour réseau, Edge peut suspendre le contexte : on le reprend.
  const wake = () => { if (sessionStorage.getItem(FLAG) === "1" && document.visibilityState === "visible") resumeCtx(); };
  document.addEventListener("visibilitychange", wake);
  window.addEventListener("focus", wake);
  window.addEventListener("pageshow", wake);
  window.addEventListener("online", wake);
  // Edge exige un geste par document : tout geste ultérieur re-tente la reprise tant que le contexte n'est pas actif.
  const re = () => { if (!isAudioUnlocked()) resumeCtx(); };
  document.addEventListener("pointerdown", re);
  document.addEventListener("keydown", re);
  try { ensureCtx().onstatechange = notifySubs; } catch (e) {}
}

if (typeof document !== "undefined") armAudio();
